import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, IconButton, TextField,
    FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { Edit, Save, Cancel, CloudUpload, Add } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { useOrgUser } from '../../context/OrgUserProvider';
import { putObject, deleteObject } from '../../api/s3Api';
import LessonManager from './LessonManager';

const CourseInfoTab = ({ courseId, courseInfo, thumbnailUrl, refreshCourseData, setCourseInfo, categories }) => {
    const [isEditingImage, setIsEditingImage] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [localLessons, setLocalLessons] = useState(courseInfo.lessons || []);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [editingField, setEditingField] = useState(null);
    const [tempValue, setTempValue] = useState('');
    const { getAccessTokenSilently } = useAuth0();
    const { orgUser } = useOrgUser();

    useEffect(() => {
        setLocalLessons(courseInfo.lessons || []);
        setHasUnsavedChanges(false);
    }, [courseInfo]);

    const handleFileChange = (file) => {
        if (file && file.type.startsWith('image/')) {
            setSelectedFile(file);
            const preview = URL.createObjectURL(file);
            setPreviewUrl(preview);
        } else {
            alert('Please select a valid image file');
        }
    };

    const handleUpdateLessons = (updatedLessons) => {
        setLocalLessons(updatedLessons);
        setHasUnsavedChanges(true);
    };

    const handleApplyChanges = async () => {
        try {
            const token = await getAccessTokenSilently({
                audience: "https://qallix-api",
            });
            const orgName = orgUser.orgName;

            const updatedCourseInfo = {
                ...courseInfo,
                lessons: localLessons,
                noOflessons: localLessons.length
            };

            await saveCourseInfo(updatedCourseInfo, token, orgName);
            setCourseInfo(updatedCourseInfo);
            setHasUnsavedChanges(false);
            alert('Changes applied successfully!');
            refreshCourseData();
        } catch (error) {
            console.error("Error applying changes:", error);
            alert("Failed to apply changes. Please try again.");
        }
    };

    const handleAddLesson = () => {
        const newLesson = {
            id: (localLessons || []).length + 1, // Add sequential ID
            title: `New Lesson ${(localLessons || []).length + 1}`,
            slug: `new-lesson-${(localLessons || []).length + 1}`,
            order: (localLessons || []).length + 1,
            status: 'Draft'
        };

        const updatedLessons = [...(localLessons || []), newLesson];
        setLocalLessons(updatedLessons);
        setHasUnsavedChanges(true);
    };

    const handleDeleteLesson = async (lessonId) => {
        try {
            const token = await getAccessTokenSilently({
                audience: "https://qallix-api",
            });
            const orgName = orgUser.orgName;

            // Delete the lesson content.json file
            const contentKey = `QallixAcademy/SoftwareCourses/${courseId}/lessons/${lessonId}/content.json`;
            const deleteContentData = {
                organization: orgName,
                bucketName: "qallix-storage",
                key: contentKey,
            };
            
            await deleteObject(token, deleteContentData);
            
            // Note: S3 doesn't have actual folders, but we can delete all objects with the prefix
            // If you want to delete all files in the lesson folder, you would need to list and delete them
            
            // Update the local lessons state
            const updatedLessons = localLessons.filter(lesson => lesson.id !== lessonId);
            setLocalLessons(updatedLessons);
            setHasUnsavedChanges(true);
            
        } catch (error) {
            console.error("Error deleting lesson files:", error);
            alert("Failed to delete lesson files. Please try again.");
        }
    };

    const handleEditClick = () => {
        setIsEditingImage(!isEditingImage);
        if (isEditingImage && selectedFile) {
            setSelectedFile(null);
            setPreviewUrl(null);
        }
    };

    const handleCancelEdit = () => {
        setIsEditingImage(false);
        setSelectedFile(null);
        setPreviewUrl(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFileChange(files[0]);
        }
    };

    const handleFileInputChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            handleFileChange(file);
        }
    };

    const getBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.onerror = (error) => reject(error);
        });
    };

    const uploadImage = async () => {
        if (!selectedFile) return;

        setUploading(true);
        try {
            const token = await getAccessTokenSilently({
                audience: "https://qallix-api",
            });
            const orgName = orgUser.orgName;

            const currentThumbnail = courseInfo.thumbnail;
            if (currentThumbnail) {
                try {
                    const deleteData = {
                        organization: orgName,
                        bucketName: "qallix-public",
                        key: `QallixAcademy/SoftwareCourses/${courseId}/${currentThumbnail}`,
                    };
                    await deleteObject(token, deleteData);
                } catch (deleteError) {
                    console.error("No thumbnail to delete or deletion failed:", deleteError);
                }
            }

            const fileExtension = selectedFile.name.split(".").pop();
            const fileName = `thumbnail.${fileExtension}`;
            const base64Data = await getBase64(selectedFile);

            const putData = {
                organization: orgName,
                putObjectItems: [
                    {
                        bucketName: "qallix-public",
                        contentType: selectedFile.type,
                        fileContent: base64Data,
                        key: `QallixAcademy/SoftwareCourses/${courseId}/${fileName}`,
                    }
                ]
            };

            const uploadResponse = await putObject(token, putData);
            if (uploadResponse.statusCode === 200) {
                const updatedCourseInfo = {
                    ...courseInfo,
                    thumbnail: fileName,
                };
                await saveCourseInfo(updatedCourseInfo, token, orgName);
                setCourseInfo(updatedCourseInfo);
                if (previewUrl) URL.revokeObjectURL(previewUrl);
                setSelectedFile(null);
                setPreviewUrl(null);
                setIsEditingImage(false);
                refreshCourseData();
            } else {
                throw new Error("Failed to upload thumbnail");
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            alert("Failed to upload image. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    const saveCourseInfo = async (updatedCourseInfo, token, orgName) => {
        try {
            const courseInfoJson = JSON.stringify(updatedCourseInfo, null, 2);
            const data = {
                organization: orgName,
                putObjectItems: [
                    {
                        bucketName: "qallix-public",
                        contentType: "application/json",
                        fileContent: btoa(courseInfoJson),
                        key: `QallixAcademy/SoftwareCourses/${courseId}/course-info.json`,
                    }
                ]
            };
            const courseInfoUploadResponse = await putObject(token, data);
            if (courseInfoUploadResponse.statusCode !== 200) {
                throw new Error("Failed to update course info");
            }
            return true;
        } catch (error) {
            console.error("Error saving course info:", error);
            throw error;
        }
    };

    const handleEditField = (field, value) => {
        setEditingField(field);
        setTempValue(value || '');
    };

    const handleSaveField = async (field) => {
        try {
            const token = await getAccessTokenSilently({
                audience: "https://qallix-api",
            });
            const orgName = orgUser.orgName;

            const updatedCourseInfo = {
                ...courseInfo,
                [field]: tempValue
            };
            await saveCourseInfo(updatedCourseInfo, token, orgName);
            setCourseInfo(updatedCourseInfo);
            setEditingField(null);
            setTempValue('');
        } catch (error) {
            console.error("Error saving field:", error);
            alert("Failed to save changes. Please try again.");
        }
    };

    const handleCancelEditField = () => {
        setEditingField(null);
        setTempValue('');
    };

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    if (!courseInfo) {
        return <Typography>No course information available</Typography>;
    }

    const levelOptions = ['Basic', 'Intermediate', 'Advanced'];
    const statusOptions = ['Draft', 'Published'];

    const renderEditableField = (field, title, value, options = null, isSlug = false) => (
        <Box sx={{ backgroundColor: "#F1F5F8", padding: "15px", borderRadius: "8px", height: 'fit-content' }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: "'Satoshi', sans-serif" }}>
                    {title}
                </Typography>
                <IconButton
                    size="small"
                    onClick={() => handleEditField(field, value)}
                    sx={{ color: "#332872" }}
                >
                    <Edit fontSize="small" />
                </IconButton>
            </Box>

            {editingField === field ? (
                <Box>
                    {options ? (
                        <FormControl fullWidth size="small">
                            <Select
                                value={tempValue}
                                onChange={(e) => setTempValue(e.target.value)}
                            >
                                {options.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <TextField
                            fullWidth
                            value={tempValue}
                            onChange={(e) => setTempValue(e.target.value)}
                            variant="outlined"
                            size="small"
                            multiline={field === 'description'}
                            rows={field === 'description' ? 4 : 1}
                            type={field === 'duration' ? 'number' : 'text'}
                            inputProps={field === 'duration' ? { min: 0, step: 0.5 } : {}}
                            helperText={isSlug ? "URL-friendly identifier (lowercase, hyphens, no spaces)" : ""}
                        />
                    )}
                    <Box sx={{ display: "flex", gap: "10px", marginTop: "10px", justifyContent: "flex-end" }}>
                        <Button
                            variant="contained"
                            onClick={() => handleSaveField(field)}
                            size="small"
                            sx={{
                                backgroundColor: "#332872",
                                "&:hover": { backgroundColor: "#241e52" },
                                fontFamily: "'Satoshi', sans-serif",
                            }}
                        >
                            <Save fontSize="small" sx={{ marginRight: "5px" }} />
                            Save
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleCancelEditField}
                            size="small"
                            sx={{
                                borderColor: "#332872",
                                color: "#332872",
                                fontFamily: "'Satoshi', sans-serif",
                            }}
                        >
                            <Cancel fontSize="small" sx={{ marginRight: "5px" }} />
                            Cancel
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Typography sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                    {field === 'duration' ? `${value || 'Not specified'} hours` : value || 'Not specified'}
                </Typography>
            )}
        </Box>
    );

    return (
        <Box>
            <Typography variant="h4" sx={{
                marginBottom: '20px',
                fontWeight: 'bold',
                fontFamily: "'Satoshi', sans-serif",
                fontSize: '20px'
            }}>
                Course Information
            </Typography>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '20px',
                alignItems: 'start',
                fontFamily: "'Satoshi', sans-serif",
            }}>
                {renderEditableField('title', 'Title', courseInfo.title)}
                {renderEditableField('description', 'Description', courseInfo.description)}
                {renderEditableField('level', 'Level', courseInfo.level, levelOptions)}

                <Box sx={{ backgroundColor: "#F1F5F8", padding: "15px", borderRadius: "8px" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: "'Satoshi', sans-serif" }}>
                            Category
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => handleEditField('category', courseInfo.category)}
                            sx={{ color: "#332872" }}
                        >
                            <Edit fontSize="small" />
                        </IconButton>
                    </Box>

                    {editingField === 'category' ? (
                        <Box>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={tempValue}
                                    onChange={(e) => setTempValue(e.target.value)}
                                >
                                    {(categories || []).map((category) => (
                                        <MenuItem key={category.name} value={category.name}>
                                            {category.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Box sx={{ display: "flex", gap: "10px", marginTop: "10px", justifyContent: "flex-end" }}>
                                <Button
                                    variant="contained"
                                    onClick={() => handleSaveField('category')}
                                    size="small"
                                    sx={{
                                        backgroundColor: "#332872",
                                        "&:hover": { backgroundColor: "#241e52" },
                                        fontFamily: "'Satoshi', sans-serif",
                                    }}
                                >
                                    <Save fontSize="small" sx={{ marginRight: "5px" }} />
                                    Save
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleCancelEditField}
                                    size="small"
                                    sx={{
                                        borderColor: "#332872",
                                        color: "#332872",
                                        fontFamily: "'Satoshi', sans-serif",
                                    }}
                                >
                                    <Cancel fontSize="small" sx={{ marginRight: "5px" }} />
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Typography sx={{ fontFamily: "'Satoshi', sans-serif" }}>
                            {courseInfo.category || 'Not specified'}
                        </Typography>
                    )}
                </Box>

                {renderEditableField('status', 'Status', courseInfo.status, statusOptions)}
                {renderEditableField('duration', 'Duration', courseInfo.duration)}

                <Box sx={{ marginBottom: "20px", backgroundColor: "#F1F5F8", padding: "15px", borderRadius: "8px" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold", fontFamily: "'Satoshi', sans-serif" }}>
                            Course Image
                        </Typography>
                        {!isEditingImage && (
                            <IconButton size="small" onClick={handleEditClick} sx={{ color: "#332872" }}>
                                <Edit fontSize="small" />
                            </IconButton>
                        )}
                    </Box>

                    {isEditingImage ? (
                        selectedFile ? (
                            <Box>
                                <img
                                    src={previewUrl}
                                    alt="Preview thumbnail"
                                    style={{
                                        width: "100%",
                                        height: "90%",
                                        borderRadius: "8px",
                                        objectFit: "cover",
                                        marginBottom: "15px"
                                    }}
                                />
                                <Box sx={{ display: "flex", gap: "10px", justifyContent: "right" }}>
                                    <Button
                                        variant="contained"
                                        onClick={uploadImage}
                                        disabled={uploading}
                                        sx={{
                                            backgroundColor: "#332872",
                                            "&:hover": { backgroundColor: "#241e52" },
                                            fontFamily: "'Satoshi', sans-serif",
                                        }}
                                    >
                                        {uploading ? "Uploading..." : "Upload Image"}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={handleCancelEdit}
                                        disabled={uploading}
                                        sx={{
                                            borderColor: "#332872",
                                            color: "#332872",
                                            fontFamily: "'Satoshi', sans-serif",
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    padding: "20px",
                                    border: isDragging ? "2px dashed #332872" : "2px dashed #ddd",
                                    borderRadius: "8px",
                                    backgroundColor: isDragging ? "#f0f0ff" : "#fafafa",
                                    textAlign: "center",
                                    transition: "all 0.3s ease",
                                }}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <CloudUpload sx={{ fontSize: 40, color: "#332872", marginBottom: "10px" }} />
                                <Typography variant="body1" sx={{ marginBottom: "10px", fontFamily: "'Satoshi', sans-serif" }}>
                                    Drag & drop an image here or click to browse
                                </Typography>
                                <input
                                    type="file"
                                    id="thumbnail-upload"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={handleFileInputChange}
                                />
                                <label htmlFor="thumbnail-upload">
                                    <Button
                                        variant="contained"
                                        component="span"
                                        sx={{
                                            backgroundColor: "#332872",
                                            "&:hover": { backgroundColor: "#241e52" },
                                            fontFamily: "'Satoshi', sans-serif",
                                        }}
                                    >
                                        Choose File
                                    </Button>
                                </label>
                                <Box sx={{ display: "flex", justifyContent: "center", marginTop: "15px" }}>
                                    <Button
                                        variant="outlined"
                                        onClick={handleCancelEdit}
                                        sx={{
                                            borderColor: "#332872",
                                            color: "#332872",
                                            fontFamily: "'Satoshi', sans-serif",
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                                <Typography variant="caption" display="block" sx={{ marginTop: "10px", color: "#666", fontFamily: "'Satoshi', sans-serif" }}>
                                    Recommended: 1920x1080px
                                </Typography>
                            </Box>
                        )
                    ) : (
                        thumbnailUrl ? (
                            <img
                                src={thumbnailUrl}
                                alt="Course thumbnail"
                                style={{
                                    width: "100%",
                                    height: "90%",
                                    borderRadius: "8px",
                                    objectFit: "cover",
                                }}
                            />
                        ) : (
                            <Box sx={{ padding: "20px", textAlign: "center", color: "#666", fontFamily: "'Satoshi', sans-serif" }}>
                                <CloudUpload sx={{ fontSize: 40, color: "#ccc", marginBottom: "10px" }} />
                                <Typography>No image uploaded</Typography>
                            </Box>
                        )
                    )}
                </Box>

                <Box sx={{ gridColumn: 'span 1', backgroundColor: "#F1F5F8", padding: "15px", borderRadius: "8px" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: "'Satoshi', sans-serif" }}>
                            Lessons
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={handleAddLesson}
                            sx={{
                                backgroundColor: "#332872",
                                "&:hover": { backgroundColor: "#241e52" },
                                fontFamily: "'Satoshi', sans-serif",
                            }}
                            startIcon={<Add />}
                        >
                            Add Lesson
                        </Button>
                    </Box>

                    <LessonManager
                        lessons={localLessons}
                        onLocalUpdate={handleUpdateLessons}
                        onDeleteLesson={handleDeleteLesson}
                        courseId={courseId}
                    />

                    {hasUnsavedChanges && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "10px", mt: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setLocalLessons(courseInfo.lessons || []);
                                    setHasUnsavedChanges(false);
                                }}
                                sx={{
                                    borderColor: "#332872",
                                    color: "#332872",
                                    fontFamily: "'Satoshi', sans-serif",
                                    '&:hover': {
                                        borderColor: "#241e52",
                                        backgroundColor: 'rgba(51, 40, 114, 0.04)'
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleApplyChanges}
                                sx={{
                                    backgroundColor: "#332872",
                                    "&:hover": { backgroundColor: "#241e52" },
                                    fontFamily: "'Satoshi', sans-serif",
                                }}
                                startIcon={<Save />}
                            >
                                Apply Changes
                            </Button>
                        </Box>
                    )}
                </Box>
                {renderEditableField('slug', 'Slug', courseInfo.slug, null, true)}
            </Box>
        </Box>
    );
};

export default CourseInfoTab;